import { filterByRegistroAnterior } from '@atendimento/detail/historico/utils/filterByRegistroAnterior'
import { Heading, InfoLabel, isEmpty, Text, VFlow } from 'bold-ui'
import { DateTime } from 'components/date'
import {
  AtendimentoProfissionalPreNatal,
  AtendimentoProfissionalPuericultura,
  EvolucaoObjetivo,
  HistoricoObjetivoPecFragmentFragment,
  MarcadorConsumoAlimentar,
  ResultadoExame,
} from 'graphql/types.generated'
import { emptyArrayOf } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { calculateAge } from 'util/date/calculateAge'
import { historicoSubsectionStyles } from 'view/atendimentos/detail/historico/model/model-historico'
import {
  convertMedicoesToHistoricoMedicaoModel,
  getMedicoesPreNatal,
  hasAllAttrsNull,
  hasMedicoes,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'
import { ArquivoAtendimentoProfissionalHistorico } from 'view/atendimentos/detail/soap/objetivo/anexo-arquivos/model-anexoArquivos'
import { IVCF_RESULTADOS } from 'view/atendimentos/detail/soap/objetivo/ivcf/model-ivcf'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import HistoricoArquivosAnexadosTableBox from '../../HistoricoArquivosAnexadosTableBox'
import { HistoricoIvcfTableBox } from '../../HistoricoIvcfTableBox'
import HistoricoMCATableBox from '../../HistoricoMCATableBox'
import HistoricoResultadoExamesTable from '../../HistoricoResultadoExamesTable'
import HistoricoMedicoesPanel from '../../medicoes/HistoricoMedicoesPanel'
import HistoricoMedicoesPreNatalTableBox from '../pre-natal/HistoricoMedicoesPreNatalTableBox'
import HistoricoObjetivoInformacoesPuericultura from '../puericultura/HistoricoObjetivoInformacoesPuericultura'
import { HistoricoMChatPanel } from '../puericultura/m-chat/HistoricoMChatPanel'

type Ivcf = HistoricoObjetivoPecFragmentFragment['ivcf']

export type MChat = HistoricoObjetivoPecFragmentFragment['mChats'][0]

export type AtendimentoProfissionalPreNatalModel = Pick<
  AtendimentoProfissionalPreNatal,
  'id' | 'gravidezPlanejada' | 'movimentacaoFetal' | 'tipoEdema'
>

export interface HistoricoObjetivoPecPanelProps {
  evolucaoObjetivo: EvolucaoObjetivo
  medicoes: HistoricoMedicaoModel[]
  isAtendObservacao?: boolean
  examesResultados?: ReadonlyArray<ResultadoExame>
  atendProfPreNatal?: AtendimentoProfissionalPreNatalModel
  atendProfPuericultura?: AtendimentoProfissionalPuericultura
  marcadorConsumoAlimentar?: MarcadorConsumoAlimentar
  ivcf?: Ivcf
  dataNascimentoCidadao?: LocalDate
  dataAtendimento?: LocalDate
  mChats?: ReadonlyArray<MChat>
  arquivosAnexados?: ReadonlyArray<ArquivoAtendimentoProfissionalHistorico>
}

export default function HistoricoObjetivoPecPanel(props: HistoricoObjetivoPecPanelProps) {
  const {
    evolucaoObjetivo,
    medicoes,
    examesResultados = emptyArrayOf<ResultadoExame>(),
    atendProfPreNatal,
    atendProfPuericultura,
    marcadorConsumoAlimentar,
    isAtendObservacao,
    ivcf,
    dataNascimentoCidadao,
    dataAtendimento,
    arquivosAnexados = emptyArrayOf<ArquivoAtendimentoProfissionalHistorico>(),
    mChats,
  } = props

  const { recentes: medicoesRecentes } = filterByRegistroAnterior<HistoricoMedicaoModel>(medicoes, 'medicaoAnterior')

  const descricao = evolucaoObjetivo?.descricao?.removeTags()

  const possuiInformacoesPuericultura: boolean =
    (atendProfPuericultura && !hasAllAttrsNull(atendProfPuericultura)) ?? false

  const hasAtendProfPreNatal: boolean =
    atendProfPreNatal?.gravidezPlanejada || atendProfPreNatal?.movimentacaoFetal || !!atendProfPreNatal?.tipoEdema

  const preNatalInfo = getMedicoesPreNatal(medicoesRecentes)

  const hasPreNatalInfo = hasAtendProfPreNatal || !isEmpty(preNatalInfo)

  const shouldDisplayMCA = !!marcadorConsumoAlimentar
  const shouldDisplayArquivosAnexados = arquivosAnexados.isNotEmpty()
  const shouldDisplayMChat = mChats?.isNotEmpty()

  const hasPuericultura = possuiInformacoesPuericultura || shouldDisplayMChat

  const hasAgeDates = !isUndefinedOrNull(dataNascimentoCidadao) && !isUndefinedOrNull(dataAtendimento)
  const ageInMonths = hasAgeDates ? calculateAge(dataNascimentoCidadao, dataAtendimento).ageInMonths : undefined

  const hasObjetivo: boolean =
    !!descricao ||
    !isEmpty(examesResultados) ||
    hasMedicoes(medicoesRecentes) ||
    hasPreNatalInfo ||
    possuiInformacoesPuericultura ||
    shouldDisplayMCA ||
    !!ivcf ||
    shouldDisplayArquivosAnexados ||
    shouldDisplayMChat ||
    false

  const sortedItems: Array<HistoricoMedicaoModel> = (medicoesRecentes || [])
    .toSorted((a, b) => a.dataMedicao - b.dataMedicao)
    .reverse()

  const { dum, vacinacaoEmDia } = resolveDumAndVacinacaoEmDia(sortedItems)

  return hasObjetivo ? (
    <VFlow>
      {descricao && <Text dangerouslySetInnerHTML={{ __html: evolucaoObjetivo.descricao }} />}

      {shouldDisplayArquivosAnexados && <HistoricoArquivosAnexadosTableBox arquivosAnexados={arquivosAnexados} />}

      {dum && (
        <InfoLabel title='DUM' titleStyles={historicoSubsectionStyles.objetivo}>
          <DateTime value={dum} format='DD/MM/YYYY' />
        </InfoLabel>
      )}

      {hasMedicoes(medicoesRecentes) && (
        <HistoricoMedicoesPanel
          sectionStyle='O'
          medicoes={medicoesRecentes}
          isAtendimentoObservacao={isAtendObservacao}
        />
      )}

      {shouldDisplayMCA && <HistoricoMCATableBox marcadorConsumoAlimentar={marcadorConsumoAlimentar} />}

      {vacinacaoEmDia && (
        <InfoLabel title='Vacinação em dia' titleStyles={historicoSubsectionStyles.objetivo}>
          Sim
        </InfoLabel>
      )}

      {ivcf && (
        <HistoricoIvcfTableBox
          ivcf={ivcf}
          riscoVulnerabilidadeText={IVCF_RESULTADOS[ivcf.riscoVulnerabilidade].longTitle}
        />
      )}

      {hasPreNatalInfo && atendProfPreNatal && (
        <HistoricoMedicoesPreNatalTableBox medicoes={preNatalInfo} atendProfPreNatal={atendProfPreNatal} />
      )}

      {hasPuericultura && (
        <VFlow vSpacing={1}>
          <Heading level={3} style={historicoSubsectionStyles.puericultura}>
            Informações de puericultura
          </Heading>

          {atendProfPuericultura && possuiInformacoesPuericultura && (
            <HistoricoObjetivoInformacoesPuericultura
              medicoes={convertMedicoesToHistoricoMedicaoModel(medicoesRecentes)}
              atendProfPuericultura={atendProfPuericultura}
            />
          )}

          {shouldDisplayMChat && <HistoricoMChatPanel mChats={mChats} idadeEmMeses={ageInMonths} />}
        </VFlow>
      )}

      {!isEmpty(examesResultados) && <HistoricoResultadoExamesTable examesResultados={examesResultados} />}
    </VFlow>
  ) : (
    <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
  )
}

const resolveDum = (items: HistoricoMedicaoModel[]) => {
  if (items.isEmpty()) return null

  const dums = items.map((item) => item.dum)

  return dums.find((dum) => !isUndefinedOrNull(dum))
}

const resolveVacinacaoEmDia = (items: HistoricoMedicaoModel[]) => {
  if (items.isEmpty()) return null

  const values = items.map((item) => item.valorVacinacaoEmDia)

  return values.find((vacinacaoEmDia) => !isUndefinedOrNull(vacinacaoEmDia))
}

const resolveDumAndVacinacaoEmDia = (items: HistoricoMedicaoModel[]) => {
  const dum = resolveDum(items)
  const vacinacaoEmDia = resolveVacinacaoEmDia(items)

  return { dum, vacinacaoEmDia }
}
